import { merge, combineLatest, NEVER, of } from 'rxjs'
import { switchMap, tap, filter, map, first } from 'rxjs/operators'
import { forEach, mapValues, range, result, startsWith, pick, flatten } from 'libraries/lodash'
import { isValidResponse, flatMapResponse, mapResponse } from 'front-end-core/helpers/dataflow'
import { QA_COMMENTS, QA_SELECTION } from 'constants/feature-flags'
import { accordionContent, accordionGroup } from 'helpers/accordion'
import { observePageState, updatePageState } from 'front-end-core/services/PageStateService'
import url from 'front-end-core/core/url'
import { QA_CONTENT as S_QA_CONTENT } from 'constants/controllers'
import { observeSelected, toggleContent } from 'front-end-core/services/StateService'
import { requestComments } from 'services/CommentsService'
import { RESPONSE_NOTFOUND } from 'front-end-core/constants/responses'
import { fetch, refresh } from 'services/ApiService'
import {
  QA_CONTENT,
  QA_CONTENT_PREPAY,
  QA_QUESTIONS,
  QA_QUESTIONS_PREPAY,
  QA_REFERENCES,
  QA_SECTIONS,
  QA_SECTIONS_PREPAY
} from 'constants/requests'
import loggedRequestSwitcher from 'helpers/loggedRequestSwitcher'
import reduxStore from 'redux/store'

export const toPath = function (itemPath) {
  return itemPath.join('/') + '/'
}

export const requestSections = function (flavour, aol) {
  return loggedRequestSwitcher(
    () => fetch(QA_SECTIONS, { flavour, aol }),
    () => fetch(QA_SECTIONS_PREPAY, { flavour, aol })
  )
}

export const refreshSections = function (flavour, aol) {
  return loggedRequestSwitcher(
    () => refresh(QA_SECTIONS, { flavour, aol }),
    () => refresh(QA_SECTIONS_PREPAY, { flavour, aol })
  )
}

export const requestFlavouredQASections = function (aol, flavours) {
  return combineLatest(
    [...flavours.map(flavour => requestSections(flavour, aol))],
    mapResponse(function () {
      return Object.assign(...flavours.map((flavour, i) => ({ [flavour]: arguments[i] })))
    })
  )
}

export const requestQAReferences = function ({ flavour, references, aol }) {
  return combineLatest([
    fetch(QA_REFERENCES, { references, aol }),
    requestSections(flavour, aol)],
  mapResponse(({ questions }, { nodes: sections }) => ({
    questions,
    sections
  }))
  )
}

const SectionContentStore = {}

/**
 * Requests list of all sections at root level
 * Requests list of all subsections and questions combined at sublevels
 * @return {Observable}
 */
export const requestSectionContent = function (flavour, sectionId, aol) {
  if (!sectionId || +sectionId < 0) {
    return requestSections(flavour, aol)
  }

  return requestSections(flavour, aol).pipe(
    switchMap(
      flatMapResponse(resp => {
        const rootId = result(resp, ['nodes', sectionId, 'path', 0], null)
        if (rootId === null) {
          return of(RESPONSE_NOTFOUND)
        }
        if (Object.prototype.hasOwnProperty.call(SectionContentStore, flavour + rootId)) {
          return of(SectionContentStore[flavour + rootId])
        } else {
          const sections = result(resp, ['nodes', rootId, 'descendants'], [])
          const paths = {}
          const nodes = pick(resp.nodes, sections)
          forEach(sections, section => {
            paths[section] = resp.nodes[section].path
          })
          const rootSection = [rootId]

          return loggedRequestSwitcher(
            () => fetch(QA_CONTENT, { flavour, sections: rootSection, aol }).pipe(
              map(
                mapResponse(content => ({ ...content, paths, nodes, rootId }))
              )
            ),
            () => fetch(QA_CONTENT_PREPAY, { flavour, sections: rootSection, aol }).pipe(
              map(
                mapResponse(content => ({ ...content, paths, nodes, rootId }))
              )
            )
          )
        }
      })
    ),
    tap(
      mapResponse(resp => {
        SectionContentStore[flavour + resp.rootId] = resp
      })
    )
  )
}

const mergeSectionQuestions = function (questions, sections) {
  const {
    all: allQuestions,
    nodes: questionNodes,
    sections: sectionReferences
  } = questions
  const { nodes: sectionNodes, root: sectionRoot } = sections

  return {
    allQuestions,
    questionNodes,
    sectionNodes,
    sectionReferences,
    sectionRoot
  }
}

export const requestSectionQuestions = function (flavour, aol) {
  const loggedRequest = () => combineLatest(
    [fetch(QA_QUESTIONS, { flavour, aol }), requestSections(flavour, aol)],
    mapResponse(mergeSectionQuestions)
  )
  const prepayRequest = () => combineLatest(
    [fetch(QA_QUESTIONS_PREPAY, { flavour, aol }), requestSections(flavour, aol)],
    mapResponse(mergeSectionQuestions)
  )

  return loggedRequestSwitcher(loggedRequest, prepayRequest)
}

/**
* Expands entire path from root to focusedId
*/
export const expandContentPath = function (flavour, rootId, focusedId, aol) {
  return requestSectionContent(flavour, rootId, aol).pipe(
    filter(isValidResponse),
    map(
      resp =>
        resp.paths[result(resp, ['questions', focusedId, 'sectionId'], focusedId)]
    ),
    tap(path => {
      path &&
        updatePageState(state => {
          const stateUpdates = { [toPath([rootId])]: 1 }
          for (let i = 0; i < path.length - 1; i++) { // for every path segment except the leaf
            stateUpdates[toPath([...path.slice(0, i + 1)])] = 1
          }
          stateUpdates[toPath([...path.slice(0, 2), 'leaf', path[path.length - 1]])] = 1 // expand the leaf
          stateUpdates[toPath([...path, 'content'])] = 1 // expand the content
          return Object.assign(
            {},
            state,
            stateUpdates
          )
        })
    })
  )
}

const listSectionTocIds = function (sectionContent, sectionId) {
  return flatten(
    result(sectionContent, ['nodes', sectionId, 'descendants'], [])
      .map(id => result(sectionContent, ['sections', id], []))
  )
}

const listSectionIds = function (sectionContent, sectionId) {
  return flatten(
    result(sectionContent, ['nodes', sectionId, 'descendants'], [])
  )
}

/**
* QA Question to AccordionContent map
* @return {Object}
*/
const toAccordionContent = function (flavour, path, item) {
  const reduxState = reduxStore.getState()
  const { aol: { name: aol } } = reduxState
  const { tocId, question: title, questionNumber, answer: content } = item
  const [sectionId, commentTarget] = path
  const itemPath = [...path, tocId]
  const expandPath = [...path, 'content']
  const commentsSource = QA_COMMENTS
    ? requestComments(commentTarget, tocId)
      .pipe(map(comments => ({ comments, commentTarget, commentCategory: flavour, commentReference: tocId })))
    : NEVER

  const stateSource = merge(
    observePageState(toPath(expandPath)).pipe(map(expanded => ({ expanded }))),
    QA_SELECTION
      ? observeSelected(flavour, [tocId]).pipe(map(state => ({ selected: state[0].selected })))
      : NEVER,
    commentsSource
  )

  return accordionContent({
    id: url(S_QA_CONTENT, { aol, flavour, sectionId, focusedId: tocId }),
    title,
    prefix: `Q${questionNumber}:`,
    path: itemPath,
    content,
    stateSource
  })
}

const s0stateMap = function (pageState) {
  let expanded = null
  forEach(pageState, (v, k) => {
    const [id, ...tail] = k.split('/')
    if (tail.length <= 1 && v > 0) {
      expanded = id
    }
  })
  return expanded
}

/**
* QA Section to AccordionGroup map
* @return {Object}
*/
const toAccordionGroup = function (flavour, item, sectionContent, aol) {
  const { path, title, id: sectionId } = item
  const [s0, s1] = path
  const id = url(S_QA_CONTENT, { aol, flavour, sectionId: s0, focusedId: sectionId })
  const tocIds = listSectionTocIds(sectionContent, sectionId)
  const sectionIds = listSectionIds(sectionContent, sectionId)
  const hasContent = Object.prototype.hasOwnProperty.call(sectionContent.sections, sectionId)
  const leafPath = toPath([s0, s1, 'leaf', sectionId])

  switch (path.length) {
    case 1:
      return accordionGroup({
        id,
        title,
        path,
        tocIds,
        sectionIds,
        groupSource: requestGroup({ flavour, parentId: sectionId, aol }),
        stateSource: observePageState().pipe(
          map(s0stateMap), // maps state to id of expanded S0
          map(expandedId => ({
            expanded: +(expandedId === sectionId),
            display: +(!expandedId || expandedId === sectionId)
          }) // leaves 1 expanded S0 displayed
          )
        )
      })
    case 2:
      return accordionGroup({
        id,
        title,
        path,
        tocIds,
        sectionIds,
        disabled: !item.active,
        groupSource: requestGroup({ flavour, parentId: sectionId, aol }),
        stateSource: merge(
          observePageState(toPath(path)).pipe(map(expanded => ({ expanded, suffix: expanded > 0 ? 'Click to collapse all' : '' })))
        )
      })
    default:
      return accordionGroup({
        id,
        title,
        tocIds,
        sectionIds,
        path: hasContent ? [s0, s1, 'leaf', sectionId] : path,
        groupSource: requestGroup({ flavour, parentId: sectionId, aol }),
        stateSource: merge(
          observePageState().pipe(map(s => ({ expanded: s[leafPath] > 0 || s[toPath(path)] > 0 }))),
          QA_SELECTION
            ? observeSelected(flavour, tocIds).pipe(map(r => ({ selected: r.reduce((a, i) => a && i.selected, 1) })))
            : NEVER
        )
      })
  }
}

/**
* Observable to feed Accordion at any level with list of Questions and Sections
* @return {Observable}
*/
export const requestGroup = function ({ flavour, parentId = '-1', aol }) {
  return requestSectionContent(flavour, parentId, aol)
    .pipe(
      map(mapResponse(
        resp => [
          ...result(resp, ['sections', parentId], []).map(tocId => toAccordionContent(flavour, resp.paths[parentId], resp.questions[tocId])), // content
          ...result(resp, ['nodes', parentId, 'childSections'], []).map(id => toAccordionGroup(flavour, resp.nodes[id], resp, aol, aol)) // sections
        ]
      )),
      map(mapResponse(
        resp => (resp.length && resp) || [accordionContent({ content: '<i>Empty</i>' })]
      ))
    )
}

export const expandCallback = function (pathStr, type, flavour, value) {
  const path = pathStr.split('/')
  const [rootId, groupId] = path
  const expandPath = path && [...path.slice(0, -1), 'content']

  switch (type) {
    case 'content':
      updatePageState(state => Object.assign({}, state, { [toPath(expandPath)]: value }))
      return true
    case 'group':
      updatePageState(state => {
        if (value <= 0) {
          state = Object.assign({}, state, ...range(path.length).map(p => ({ [toPath(path.slice(0, p))]: 1 })))
          state = mapValues(state, (v, p) =>
            (startsWith(p, pathStr) || p === toPath([rootId, groupId, 'leaf'])) ||
            p === toPath([rootId, groupId, 'leaf', path[path.length - 1]])
              ? -1
              : v) // collapse the leaf of the content
        } else {
          const s1 = toPath(path.slice(0, 2))
          state = mapValues(state, (v, p) => !startsWith(p, s1) ? -1 : v)
          state = Object.assign({}, state, ...range(path.length).map(p => ({ [toPath(path.slice(0, p + 1))]: 1 })))
        }
        return state
      })
      return true
  }
}

export const selectCallback = function (path, type, flavour, value, aol) {
  const tocId = path && path.split('/').pop()
  const sectionId = path && path.split('/').pop()
  switch (type) {
    case 'content':
      toggleContent(value, flavour, tocId)
      return true
    case 'group':
      requestSectionContent(flavour, sectionId, aol).pipe(
        filter(isValidResponse),
        first(),
        map(sectionContent => {
          return toggleContent(value, flavour, listSectionTocIds(sectionContent, sectionId))
        })
      ).subscribe(x => { })
      return true
  }
}
